import { fullMonthAndYear } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import AdNetworkSignalEvent from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/AdNetworkSignalEvent";
import SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";
import TechnologyChangesType from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologyChangesType";
import dateTimeService from "app/services/date-time/dateTimeService";

import type { CompanyBaseInfo } from "../../../types/company";
import type {
    AdNetworkSignalEventDto,
    EcommerceSignalEventDto,
    IntentSignalEventDto,
    NewsSignalEventDto,
    SignalEventDto,
    TechnologySignalEventDto,
} from "../../../types/signals";
import { signalTitleTextMap } from "../maps/newsSignalTextMap";

import { getEcommerceSignalEventHeadline } from "./getEcommerceSignalEventInfoText";
import { getTechnologySignalEventActionText } from "./getTechnologySignalEventActionText";
import { getTrafficSignalEventTitle } from "./getTrafficSignalEventTitle";
import { getIntentSignalEventsFromDto } from "./intent";

const getNewsSignalEventTitle = (signal: NewsSignalEventDto, companyName: string): string => {
    const { categories } = signal;
    const subtype = categories[0];

    const signalTitleTextMapValue = signalTitleTextMap[subtype];

    return typeof signalTitleTextMapValue === "function"
        ? signalTitleTextMapValue(signal, companyName)
        : signalTitleTextMapValue;
};

const getTechnologySignalTechnologiesText = (signal: TechnologySignalEventDto): string => {
    const { technologies, changeType } = signal;

    if (changeType === TechnologyChangesType.RenewalComingUp && technologies.length > 1) {
        if (technologies.length === 2) {
            return technologies.join(" and ");
        }

        const list = [
            ...technologies.slice(0, technologies.length - 1),
            `and ${technologies[technologies.length - 1]}`,
        ];

        return list.join(", ");
    }

    return technologies.length === 1 ? technologies[0] : `${technologies.length} technologies`;
};

const getTechnologySignalRenewalDate = (signal: TechnologySignalEventDto): string => {
    const { changeType, renewalDate } = signal;

    if (changeType !== TechnologyChangesType.RenewalComingUp || !renewalDate) {
        return "";
    }

    return dateTimeService.formatWithMoment(renewalDate, fullMonthAndYear);
};

const getTechnologySignalEventTitle = (signal: TechnologySignalEventDto, companyName: string): string => {
    const technologyText = getTechnologySignalTechnologiesText(signal);
    const renewalDateText = getTechnologySignalRenewalDate(signal);

    return [
        companyName,
        getTechnologySignalEventActionText(signal),
        technologyText,
        renewalDateText ? `in ${renewalDateText}` : null,
    ]
        .filter((v) => v)
        .join(" ");
};

const getIntentSignalEventTitle = (signal: IntentSignalEventDto, companyName: string): string => {
    const signalEvents = getIntentSignalEventsFromDto(signal);
    const signalEventsCount = signalEvents.length;

    return translate(
        signalEventsCount > 1
            ? "si.signals.intent.company-overview.title"
            : "si.signals.intent.company-overview.title.single",
        {
            companyName,
            topicsAmount: signalEventsCount,
        },
    );
};

const getAdNetworkSignalEventActionText = (signal: AdNetworkSignalEventDto, companyName: string): string => {
    const { changeType } = signal;

    const actionText =
        changeType === AdNetworkSignalEvent.StartedWorkingWith ? "started to work with" : "stopped working with";
    return `${companyName} ${actionText}`;
};

const getAdNetworkSignalEventTitle = (signal: AdNetworkSignalEventDto, companyName: string): string => {
    const { adNetworkIds, adNetworkNames } = signal;
    if (adNetworkIds.length > 1) {
        return `${getAdNetworkSignalEventActionText(signal, companyName)} ${adNetworkIds.length} ad-networks`;
    }

    return `${getAdNetworkSignalEventActionText(signal, companyName)} ${adNetworkNames[0]}`;
};

const getEcommerceSignalEventTitle = (signal: EcommerceSignalEventDto, companyName: string): string => {
    const headline = getEcommerceSignalEventHeadline(signal);
    if (!companyName) {
        return headline;
    }
    return headline ? `${companyName} — ${headline}` : headline;
};

export const getSignalEventTitle = (signal: SignalEventDto, companyInfo: CompanyBaseInfo | undefined): string => {
    const { type } = signal;
    const companyName = companyInfo?.name || "";
    const domain = companyInfo?.mainDomain || "";

    if (type === SignalType.News) {
        return getNewsSignalEventTitle(signal, companyName);
    } else if (type === SignalType.Technology) {
        return getTechnologySignalEventTitle(signal, companyName);
    } else if (type === SignalType.AdNetwork) {
        return getAdNetworkSignalEventTitle(signal, companyName);
    } else if (type === SignalType.Traffic) {
        return getTrafficSignalEventTitle(signal, domain);
    } else if (type === SignalType.Intent) {
        return getIntentSignalEventTitle(signal, companyName);
    } else if (type === SignalType.Ecommerce) {
        return getEcommerceSignalEventTitle(signal, companyName);
    }
    return "";
};
